import { Modal, Select, Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import Icon from '@ant-design/icons';
import { dimensionMaxLimit } from '@components/dimensions/components/PresetPanel';
import { IDimensionInfo } from '@components/dimensions/typed';
import { partitionOptions } from '@components/dimensions/utils/partition';
import AddDimensionsIcon from '@icons/dimensions/add-dimensions.svg';

const ButtonContainer = styled.span<{ disabled?: boolean }>`
  cursor: pointer;
  display: inline-flex;
  gap: 4px;
  color: rgba(0, 0, 0, 0.45);

  ${(props) => {
    if (props?.disabled) {
      return css`
        cursor: not-allowed;
        opacity: 0.5;
      `;
    }
    return;
  }}
`;

const BaseButton = () => {
  return (
    <>
      <Icon component={AddDimensionsIcon} style={{ display: 'flex', alignItems: 'center' }} />
      <span>Add Dimension</span>
    </>
  );
};

const AddDimensionButtonWithToolTip = ({
  disabled = false,
  onClick,
}: {
  disabled?: boolean;
  onClick?: () => void;
}) => {
  return (
    <>
      {disabled ? (
        <Tooltip title={`Reached max limit of ${dimensionMaxLimit}`}>
          <ButtonContainer disabled={disabled} onClick={onClick}>
            <BaseButton />
          </ButtonContainer>
        </Tooltip>
      ) : (
        <ButtonContainer disabled={disabled} onClick={onClick}>
          <BaseButton />
        </ButtonContainer>
      )}
    </>
  );
};

interface IAddDimensionButton {
  data: any;
  onAddDimension: (dimension: IDimensionInfo) => void;
  disabled?: boolean;
}

export const AddDimensionButton: React.FC<IAddDimensionButton> = (props) => {
  const { data, onAddDimension, disabled } = props;
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string>();

  const handleOpenModal = () => {
    !disabled && setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setValue(undefined);
  };

  const handleCancel = () => {
    closeModal();
  };

  const handleOk = () => {
    const item = (data ?? []).find((i) => i.dimension_name === value);
    if (item) {
      onAddDimension({
        dimensionName: item.dimension_name,
        displayName: item.display_name,
        dimensionType: item.dimension_type,
      });
    }
    closeModal();
  };

  const handleSelectChange = (value: string) => {
    setValue(value);
  };

  const options = useMemo(() => {
    const { options } = partitionOptions(data, (dimension) => dimension.existing);
    return options;
  }, [data]);

  return (
    <>
      <AddDimensionButtonWithToolTip disabled={disabled} onClick={handleOpenModal} />
      <Modal
        title="Add new dimension"
        open={modalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        closable
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Select a dimension"
          options={options}
          value={value}
          onChange={handleSelectChange}
          showSearch
          filterOption={(input, option) =>
            ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
      </Modal>
    </>
  );
};
